import { Scrim } from "@/components/ui/Scrim";
import { Surface } from "@/components/ui/Surface";
import { useModalLayer } from "@/contexts/ModalContext";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { cn } from "@/lib/cn";
import { DURATION } from "@/lib/motion";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef, useState, type ReactNode, type RefObject, type TouchEvent } from "react";

const SLIDE_MS = DURATION.base * 1000;
const SLIDE_CLASS = "duration-base ease-standard";
const EDGE_PX = 50;
const MOVE_THRESHOLD_PX = 10;

export type FullscreenModalProps = {
  open: boolean;
  children: ReactNode;
  /** Visually hidden Dialog.Title. */
  title: string;
  contentRef?: RefObject<HTMLDivElement>;
  onClose?: () => void;
  /** Called by a swipe from the left edge; falls back to `onClose`. */
  onSwipeClose?: () => void;
  /** Keeps children mounted during the exit slide instead of unmounting them as soon as `open` turns false. */
  keepChildrenWhileClosing?: boolean;
};

/**
 * Full-screen page-like modal that slides in from the right and closes with a swipe from the left edge.
 * One duration token drives both the CSS transition and the unmount timeout.
 */
export function FullscreenModal({
  open,
  children,
  title,
  contentRef,
  onClose,
  onSwipeClose,
  keepChildrenWhileClosing = false,
}: FullscreenModalProps) {
  const [visible, setVisible] = useState(open);
  const [entered, setEntered] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const { zIndex, backdropZIndex } = useModalLayer(visible || open);
  useLockBodyScroll(visible);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setEntered(false);
      const frame = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(frame);
    }
    setEntered(false);
    const timeout = setTimeout(() => setVisible(false), SLIDE_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  function handleTouchStart(event: TouchEvent) {
    if (!visible || !contentRef?.current) return;
    const touch = event.touches[0];
    if (touch.clientX > EDGE_PX) return;
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    setDragging(true);
  }

  function handleTouchMove(event: TouchEvent) {
    if (!dragging) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - dragStart.current.x;
    const deltaY = touch.clientY - dragStart.current.y;
    if (Math.abs(deltaX) < MOVE_THRESHOLD_PX && Math.abs(deltaY) < MOVE_THRESHOLD_PX) return;
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 0) {
      setDragOffset(Math.min(deltaX, window.innerWidth));
      return;
    }
    setDragging(false);
    setDragOffset(0);
  }

  function handleTouchEnd() {
    if (!dragging) return;
    if (dragOffset > window.innerWidth * 0.1) (onSwipeClose ?? onClose)?.();
    setDragging(false);
    setDragOffset(0);
  }

  if (!visible && !open) return null;

  const fadeStart = window.innerWidth * 0.25;
  const dragOpacity = Math.max(0, 1 - Math.max(0, (dragOffset - fadeStart) / fadeStart));

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <Scrim
            zIndex={backdropZIndex}
            className={cn("transition-opacity", SLIDE_CLASS, entered ? "opacity-100" : "opacity-0")}
          />
        </Dialog.Overlay>
        <Dialog.Content
          asChild
          aria-describedby={undefined}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Surface
            surface="page"
            className={cn(
              "fixed left-0 top-0 h-full w-full overflow-hidden focus:outline-none",
              dragging ? "transition-none" : cn("transition-[transform,opacity]", SLIDE_CLASS)
            )}
            style={{
              zIndex,
              transform: dragging ? `translateX(${dragOffset}px)` : entered ? "translateX(0)" : "translateX(100%)",
              opacity: dragging ? dragOpacity : entered ? 1 : 0,
            }}
          >
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            <div
              ref={contentRef}
              className="relative h-full max-h-screen w-full touch-pan-y overflow-y-auto overflow-x-clip overscroll-y-none"
            >
              <div className="relative mx-auto h-full w-full max-w-shell">
                {open || keepChildrenWhileClosing ? children : null}
              </div>
            </div>
          </Surface>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
